/**
 * Shared primitives for the public Onboarding form.
 *
 * Includes:
 *   • Layout/decoration: FloatingShapes, ProgressIndicator, SectionHero, NavigationBar, DoneScreen
 *   • Inputs: Field, TextInput, TextArea, Toggle, CheckChip
 *   • Style helpers: inputBase, inputStyle
 *   • Constants: US_STATES, SHIRT_SIZES
 */
import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Confetti, CheckCircle, Info, Warning,
} from '@phosphor-icons/react';

export const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
  ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'], ['PR', 'Puerto Rico'],
];

export const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

export const inputBase = `w-full px-3.5 py-2.5 rounded-xl text-sm transition-all outline-none
                          border bg-transparent focus:scale-[1.01]`;

export function inputStyle(isLight) {
  return {
    color: isLight ? '#0f172a' : '#F5F5F5',
    borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.10)',
    background: isLight ? '#FFFFFF' : 'rgba(10,10,14,0.6)',
  };
}

export function FloatingShapes({ isLight }) {
  const c1 = isLight ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.12)';
  const c2 = isLight ? 'rgba(205,4,11,0.10)' : 'rgba(205,4,11,0.18)';
  const c3 = isLight ? 'rgba(168,85,247,0.10)' : 'rgba(168,85,247,0.16)';
  return (
    <>
      <motion.div className="absolute pointer-events-none rounded-full -top-32 -left-24 w-72 h-72 blur-3xl"
        style={{ background: c1 }}
        animate={{ x: [0, 20, 0], y: [0, 10, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }} />
      <motion.div className="absolute pointer-events-none rounded-full top-1/2 -right-20 w-80 h-80 blur-3xl"
        style={{ background: c2 }}
        animate={{ x: [0, -15, 0], y: [0, 25, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }} />
      <motion.div className="absolute pointer-events-none rounded-full bottom-0 left-1/3 w-64 h-64 blur-3xl"
        style={{ background: c3 }}
        animate={{ x: [0, 10, 0], y: [0, -20, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }} />
    </>
  );
}

export function ProgressIndicator({ total, current, sections, onJump, isLight, applicantName }) {
  const first = (applicantName || '').trim().split(/\s+/)[0];
  const friendlyTag = (() => {
    if (current === 0) return first ? `Welcome${first ? `, ${first}` : ''} · let's get started` : "Let's get started";
    if (current >= total - 1) return first ? `Almost done, ${first}!` : 'Almost done!';
    if (current * 2 < total) return first ? `Just getting going, ${first}` : 'Just getting going';
    return first ? `Halfway there, ${first}` : 'Halfway there';
  })();
  return (
    <div className="space-y-1.5" data-testid="progress-block">
      <div className="text-[10px] uppercase tracking-[0.22em] font-black"
        style={{ color: isLight ? '#94a3b8' : '#737373' }}
        data-testid="progress-friendly-tag">
        {friendlyTag}
      </div>
      <div className="flex items-center gap-1.5 overflow-x-auto" data-testid="progress-indicator">
        {sections.map((s, i) => {
          const Icon = s.icon;
          const active = i === current;
          const done = i < current;
          return (
            <button key={s.id} onClick={() => onJump?.(i)}
              disabled={i > current}
              className={`shrink-0 flex items-center gap-1.5 px-2 py-1.5 rounded-full transition-all
                       ${i > current ? 'cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}`}
              style={{
                background: active ? `linear-gradient(135deg, ${s.gradient[0]}, ${s.gradient[1]})`
                                    : (done ? (isLight ? '#F1F5F9' : 'rgba(20,20,28,0.6)')
                                            : 'transparent'),
                border: active ? 'none' : `1px solid ${isLight ? '#E2E8F0' : 'rgba(255,255,255,0.08)'}`,
              }}
              data-testid={`progress-step-${i}`}>
              <Icon size={11} weight="bold"
                style={{ color: active ? '#0B0B0F' : (done ? (isLight ? '#0f172a' : '#F5F5F5') : (isLight ? '#94a3b8' : '#737373')) }} />
              <span className="text-[10px] font-black uppercase tracking-wider whitespace-nowrap"
                style={{ color: active ? '#0B0B0F' : (done ? (isLight ? '#0f172a' : '#F5F5F5') : (isLight ? '#94a3b8' : '#737373')) }}>
                {s.id}
              </span>
            </button>
          );
        })}
        <span className="text-[10px] ml-2 font-black tabular-nums"
          style={{ color: isLight ? '#64748b' : '#A3A3A3' }}>
          {current + 1}/{total}
        </span>
      </div>
    </div>
  );
}

export function SectionHero({ section, applicantName }) {
  const Icon = section.icon;
  const title = (() => {
    if (!applicantName || section.id !== 'personal') return section.title;
    const first = applicantName.trim().split(/\s+/)[0];
    if (!first || first.length < 2) return section.title;
    return `Hey, ${first}.`;
  })();
  return (
    <div className="relative px-6 py-7 sm:py-8 overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${section.gradient[0]}, ${section.gradient[1]})` }}>
      <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full opacity-25 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.7), transparent 65%)' }} />
      <div className="absolute -bottom-12 -left-8 w-40 h-40 rounded-full opacity-15 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.6), transparent 60%)' }} />
      <div className="relative z-10 flex items-center gap-4">
        <motion.div
          initial={{ rotate: -8, scale: 0.8, opacity: 0 }}
          animate={{ rotate: 0, scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.2, 0.9, 0.3, 1] }}
          className="w-14 h-14 rounded-2xl inline-flex items-center justify-center shrink-0"
          style={{ background: 'rgba(11,11,15,0.92)', boxShadow: '0 12px 28px -10px rgba(0,0,0,0.55)' }}>
          <Icon size={26} weight="fill" style={{ color: section.gradient[1] }} />
        </motion.div>
        <div>
          <motion.h1
            key={title}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="text-xl sm:text-2xl font-black tracking-tight"
            style={{ color: '#0B0B0F', fontFamily: 'Outfit, sans-serif' }}>
            {title}
          </motion.h1>
          <p className="text-xs sm:text-sm" style={{ color: 'rgba(11,11,15,0.78)' }}>
            {section.subtitle}
          </p>
        </div>
      </div>
    </div>
  );
}

export function NavigationBar({ step, total, canAdvance, submitting, onBack, onNext, onSubmit, isLight }) {
  const isLast = step === total - 1;
  return (
    <div className="px-5 sm:px-7 py-4 flex items-center justify-between gap-3 border-t"
      style={{ borderColor: isLight ? 'rgba(15,23,42,0.06)' : 'rgba(255,255,255,0.06)' }}>
      <button
        type="button"
        onClick={onBack}
        disabled={step === 0}
        className="inline-flex items-center gap-1.5 text-[12px] font-black px-3 py-2 rounded-lg transition-all"
        style={{
          color: step === 0 ? (isLight ? '#cbd5e1' : '#404040') : (isLight ? '#0f172a' : '#F5F5F5'),
          background: 'transparent',
          opacity: step === 0 ? 0.4 : 1,
        }}
        data-testid="onboarding-back-btn">
        <ArrowLeft size={12} weight="bold" /> Back
      </button>

      {!isLast && (
        <button
          type="button"
          onClick={onNext}
          disabled={!canAdvance}
          className="inline-flex items-center gap-1.5 text-[12px] font-black px-4 py-2.5 rounded-xl transition-all"
          style={{
            background: canAdvance ? 'linear-gradient(135deg, #CD040B, #F59E0B)' : (isLight ? '#E2E8F0' : '#1F1F1F'),
            color: canAdvance ? '#fff' : (isLight ? '#94a3b8' : '#525252'),
            boxShadow: canAdvance ? '0 8px 22px -8px rgba(245,158,11,0.55)' : 'none',
            opacity: canAdvance ? 1 : 0.65,
            cursor: canAdvance ? 'pointer' : 'not-allowed',
          }}
          data-testid="onboarding-next-btn">
          Continue <ArrowRight size={12} weight="bold" />
        </button>
      )}

      {isLast && (
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canAdvance || submitting}
          className="inline-flex items-center gap-1.5 text-[12px] font-black px-4 py-2.5 rounded-xl transition-all"
          style={{
            background: canAdvance ? 'linear-gradient(135deg, #10B981, #06B6D4)' : (isLight ? '#E2E8F0' : '#1F1F1F'),
            color: canAdvance ? '#0B0B0F' : (isLight ? '#94a3b8' : '#525252'),
            boxShadow: canAdvance ? '0 8px 22px -8px rgba(16,185,129,0.55)' : 'none',
            opacity: canAdvance && !submitting ? 1 : 0.65,
            cursor: canAdvance && !submitting ? 'pointer' : 'not-allowed',
          }}
          data-testid="onboarding-submit-btn">
          <Confetti size={13} weight="fill" />{submitting ? 'Sending…' : 'Submit application'}
        </button>
      )}
    </div>
  );
}

export function Field({ label, hint, required, error, children, icon, testId }) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <label className="text-[11px] font-black uppercase tracking-[0.16em] flex items-center gap-1.5"
        style={{ color: 'var(--cs-text-muted)' }}>
        {icon}
        {label}
        {required && <span style={{ color: '#EF4444' }}>*</span>}
      </label>
      {children}
      {hint && !error && (
        <div className="text-[10px] flex items-center gap-1" style={{ color: 'var(--cs-text-muted)' }}>
          <Info size={9} weight="fill" /> {hint}
        </div>
      )}
      {error && (
        <div className="text-[10px] flex items-center gap-1" style={{ color: '#EF4444' }}>
          <Warning size={9} weight="fill" /> {error}
        </div>
      )}
    </div>
  );
}

export function TextInput({ value, onChange, placeholder, type = 'text', isLight, testId, autoComplete }) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className={inputBase + ' focus:border-[#F59E0B]'}
      style={inputStyle(isLight)}
      data-testid={testId}
    />
  );
}

export function Toggle({ value, onChange, options, isLight, testId }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${options.length}, 1fr)` }}
      data-testid={testId}>
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onChange(opt.value)}
            className="px-3 py-2 rounded-xl text-xs font-black transition-all"
            style={{
              background: active ? 'linear-gradient(135deg, #CD040B, #F59E0B)'
                                 : (isLight ? '#FFFFFF' : 'rgba(10,10,14,0.6)'),
              color: active ? '#fff' : (isLight ? '#0f172a' : '#F5F5F5'),
              border: `1px solid ${active ? 'transparent' : (isLight ? '#E2E8F0' : 'rgba(255,255,255,0.10)')}`,
              boxShadow: active ? '0 8px 22px -10px rgba(245,158,11,0.5)' : 'none',
            }}
            data-testid={`${testId}-${opt.value}`}>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function DoneScreen({ onBack, isLight, applicantName }) {
  const first = (applicantName || '').trim().split(/\s+/)[0];

  React.useEffect(() => {
    let cancelled = false;
    import('canvas-confetti').then(({ default: confetti }) => {
      if (cancelled) return;
      const fire = (opts) => confetti({
        spread: 70,
        startVelocity: 35,
        ticks: 90,
        gravity: 0.95,
        scalar: 1.0,
        colors: ['#CD040B', '#F59E0B', '#F5D64D', '#10B981', '#06B6D4', '#A855F7'],
        ...opts,
      });
      fire({ particleCount: 80, origin: { x: 0.15, y: 0.4 }, angle: 60 });
      fire({ particleCount: 80, origin: { x: 0.85, y: 0.4 }, angle: 120 });
      setTimeout(() => fire({ particleCount: 50, origin: { x: 0.5, y: 0.25 } }), 350);
      setTimeout(() => fire({ particleCount: 60, origin: { x: 0.5, y: 0.4 } }), 850);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden"
      style={{
        background: isLight
          ? 'radial-gradient(circle at 50% 0%, #DCFCE7 0%, #FEF3C7 50%, #FFFFFF 100%)'
          : 'radial-gradient(circle at 50% 0%, #052E16 0%, #0A0A0A 60%)',
      }}
      data-testid="onboarding-done">
      <FloatingShapes isLight={isLight} />
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55, ease: [0.2, 0.9, 0.3, 1] }}
        className="relative z-10 max-w-md w-full rounded-3xl p-8 text-center shadow-2xl"
        style={{
          background: isLight ? 'rgba(255,255,255,0.92)' : 'rgba(20,20,28,0.85)',
          border: `1px solid ${isLight ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.08)'}`,
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
        }}>
        <motion.div
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.6, delay: 0.15, type: 'spring' }}
          className="w-20 h-20 mx-auto rounded-3xl inline-flex items-center justify-center mb-4"
          style={{ background: 'linear-gradient(135deg, #10B981, #06B6D4)',
                   boxShadow: '0 18px 36px -12px rgba(16,185,129,0.55)' }}>
          <Confetti size={36} weight="fill" style={{ color: '#0B0B0F' }} />
        </motion.div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight mb-1"
          style={{ color: isLight ? '#0f172a' : '#F5F5F5', fontFamily: 'Outfit, sans-serif' }}>
          {first ? `Welcome aboard, ${first}.` : "You're in the queue!"}
        </h1>
        <p className="text-sm mb-5" style={{ color: isLight ? '#64748b' : '#A3A3A3' }}>
          Thanks for applying. A hiring manager will reach out within a couple of business days.
          {' '}Keep an eye on your email and phone.
        </p>
        <button onClick={onBack}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black"
          style={{ background: 'linear-gradient(135deg, #CD040B, #F59E0B)', color: '#fff' }}
          data-testid="onboarding-done-back">
          <ArrowLeft size={13} weight="bold" /> Back to sign in
        </button>
      </motion.div>
    </div>
  );
}

/* CheckCircle re-export so child sections don't need a separate import path */
export { CheckCircle };
